import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import sectionTypesData from '../utils/sectionTypes.json';
import useSectionManagement from './useSectionManagement';
import useQueueSettings from './useQueueSettings';
import { logger } from '../../../shared/utils/logger.js';
import { storage } from '../../../shared/utils/storage.js';

const SECTION_WING_CONFLICT_CONFIRMATION_QUESTION =
    'A section with the same type already exists in this floor and zone. Do you want to create another one?';

const DEFAULT_SECTION_CREATE_CONFLICT = {
    open: false,
    question: SECTION_WING_CONFLICT_CONFIRMATION_QUESTION,
    payload: null
};
const CHILDREN_CACHE_TTL_MS = 30000;
const CHILDREN_LOG_MODULE = 'useStoreManageController.children';

const normalizeZoneName = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
const ZONE_MINOR_WORDS = new Set(['and', 'or', 'the', 'a', 'an', 'of', 'in', 'on', 'at', 'to', 'for', 'by']);
const serializeZoneName = (value) => {
    const normalized = normalizeZoneName(value);
    if (!normalized) {
        return '';
    }
    return normalized
        .split(' ')
        .map((word, index) => {
            if (!word) {
                return word;
            }
            if (index > 0 && ZONE_MINOR_WORDS.has(word)) {
                return word;
            }
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
};

const useStoreManageController = ({
  sections = [],
  request,
  onToast,
  onSectionsUpdated,
  onZoneSaved,
  page = 'landing',
  onNavigate,
  storeToken,
  storeUuid,
  storeMallUuid,
  storeName
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formValues, setFormValues] = useState({
        sectionName: '',
        sectionType: '',
        sectionWindows: '',
        sectionWing: '',
        floorNumber: '',
        gracePeriodMins: ''
    });
    const [isCounterModalOpen, setIsCounterModalOpen] = useState(false);
    const [counterSection, setCounterSection] = useState(null);
    const [counterList, setCounterList] = useState([]);
    const [isCounterLoading, setIsCounterLoading] = useState(false);
    const [counterForm, setCounterForm] = useState({ count: 1 });
    const [isCounterSaving, setIsCounterSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [sectionStats, setSectionStats] = useState({});
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [sortKey, setSortKey] = useState('NAME');
    const [expandedSections, setExpandedSections] = useState(new Set());
    const [childrenMap, setChildrenMap] = useState({});
    const [childLoadingBySection, setChildLoadingBySection] = useState({});
    const [childLoadedBySection, setChildLoadedBySection] = useState({});
    const [editSection, setEditSection] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEditSaving, setIsEditSaving] = useState(false);
    const [editFormValues, setEditFormValues] = useState({ sectionName: '', sectionType: '', sectionWing: '', floorNumber: '', gracePeriodMins: '', sectionWindows: '' });
    const [deleteSectionTarget, setDeleteSectionTarget] = useState(null);
    const [isDeletingSection, setIsDeletingSection] = useState(false);
    const [isZoneModalOpen, setIsZoneModalOpen] = useState(false);
    const [zoneFormValues, setZoneFormValues] = useState({ floorNumber: '', zoneName: '' });
    const [zonesByFloor, setZonesByFloor] = useState({});
    const [isZoneLoading, setIsZoneLoading] = useState(false);
    const [isZoneSaving, setIsZoneSaving] = useState(false);
    const [sectionCreateConflict, setSectionCreateConflict] = useState(DEFAULT_SECTION_CREATE_CONFLICT);
    const [isSectionCreateConflictSaving, setIsSectionCreateConflictSaving] = useState(false);
    const [activeRequestCount, setActiveRequestCount] = useState(0);
    const [sectionFormError, setSectionFormError] = useState('');
    const [zoneFormError, setZoneFormError] = useState('');
    const [counterFormError, setCounterFormError] = useState('');
    const [editFormError, setEditFormError] = useState('');

    const [showSettingsSaveModal, setShowSettingsSaveModal] = useState(false);
    const isGlobalRequestLoading = activeRequestCount > 0;
    const childrenCacheRef = useRef(new Map());
    const childrenInFlightRef = useRef(new Map());

    const runWithGlobalLoader = useCallback(async (executor) => {
        setActiveRequestCount((prev) => prev + 1);
        try {
            return await executor();
        } finally {
            setActiveRequestCount((prev) => Math.max(0, prev - 1));
        }
    }, []);

    const trackedRequest = useCallback((url, options) => {
        return runWithGlobalLoader(() => request(url, options));
    }, [request, runWithGlobalLoader]);

    const {
        fetchChildren,
        fetchZones,
        createZone,
        createSection,
        updateSection,
        deleteSection,
        setSectionVisibility,
        addChild,
        setCounterVisibility,
        deleteCounter
    } = useSectionManagement({
        request: trackedRequest,
        storeToken,
        onToast
    });

    const {
        queueSettings,
        handleQueueSettingChange,
        fetchQueueSettings,
        saveQueueSettings,
        isSettingsSaving
    } = useQueueSettings({
        request: trackedRequest,
        storeToken,
        onToast
    });
    const fetchChildrenRef = useRef(fetchChildren);
    const fetchQueueSettingsRef = useRef(fetchQueueSettings);

    useEffect(() => {
        fetchChildrenRef.current = fetchChildren;
    }, [fetchChildren]);

    useEffect(() => {
        fetchQueueSettingsRef.current = fetchQueueSettings;
    }, [fetchQueueSettings]);

    const resolvedQueueSettingsToken = useMemo(() => storeToken || '', [storeToken]);

    const handleSettingsSave = async () => {
        try {
            await saveQueueSettings();
            setShowSettingsSaveModal(false);
        } catch (error) {
            logger.error('StoreManage', 'Queue settings save failed', {
                source: { file: 'features/hierarchy/hooks/useStoreManageController.js', method: 'handleSettingsSave' },
                error: error instanceof Error
                    ? { name: error.name, message: error.message, stack: error.stack }
                    : String(error)
            });
        }
    };

    const handleDownloadQR = async (section) => {
        if (!section?.sectionUuid) {
            onToast?.({ message: 'Missing section UUID for QR generation.', variant: 'error' });
            return;
        }

        const posterId = `qr-poster-${section.sectionUuid}`;
        const posterEl = document.getElementById(posterId);
        if (!posterEl) {
            onToast?.({ message: 'Unable to find QR poster element.', variant: 'error' });
            return;
        }

        const originalDisplay = posterEl.style.display;
        // Temporarily make it visible off-screen for html2canvas
        posterEl.style.display = 'flex';
        posterEl.style.position = 'fixed';
        posterEl.style.top = '-9999px';
        posterEl.style.left = '-9999px';

        try {
            const canvas = await html2canvas(posterEl, {
                scale: 3, // High resolution
                useCORS: true,
                backgroundColor: null,
                logging: false
            });

            const pngUrl = canvas.toDataURL("image/png");
            const downloadLink = document.createElement("a");
            downloadLink.href = pngUrl;
            downloadLink.download = `${storeName || 'Store'}_${section.sectionName || 'Section'}_QR.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        } catch (err) {
            logger.error('StoreManage', 'Error rendering QR poster', {
                source: { file: 'features/hierarchy/hooks/useStoreManageController.js', method: 'handleDownloadQR' },
                error: err instanceof Error
                    ? { name: err.name, message: err.message, stack: err.stack }
                    : String(err)
            });
            onToast?.({ message: 'Failed to generate QR poster.', variant: 'error' });
        } finally {
            // Restore hidden state
            posterEl.style.display = originalDisplay;
            posterEl.style.position = 'absolute';
            posterEl.style.top = '0';
            posterEl.style.left = '0';
            posterEl.style.zIndex = '-1';
        }
    };


    const PARENT_TYPES = useMemo(() => new Set(['BILLING_SECTION', 'TRIAL_SECTION', 'EXCHANGE_SECTION']), []);
    const parentSections = useMemo(() => {
        const seen = new Set();
        return sections.filter((section) => {
            const sectionUuid = String(section?.sectionUuid || '').trim();
            if (!sectionUuid || seen.has(sectionUuid)) {
                return false;
            }
            const sectionType = String(section?.sectionType || '').trim();
            const parentSectionUuid = String(section?.parentSectionUuid || '').trim();
            const hasLinkedParent = Boolean(parentSectionUuid) && parentSectionUuid !== sectionUuid;
            const isParent = PARENT_TYPES.has(sectionType) && !hasLinkedParent;
            if (!isParent) {
                return false;
            }
            seen.add(sectionUuid);
            return true;
        });
    }, [sections, PARENT_TYPES]);
    const parentSectionUuids = useMemo(
        () => parentSections.map((section) => section?.sectionUuid).filter(Boolean),
        [parentSections]
    );

    const toCounterStats = useCallback((children) => {
        const safeChildren = Array.isArray(children) ? children : [];
        const total = safeChildren.length;
        const active = safeChildren.filter((child) => child?.rowStatus === 1).length;
        const hidden = safeChildren.filter((child) => child?.rowStatus === 0).length;
        return { total, active, hidden };
    }, []);

    const writeChildrenState = useCallback((sectionUuid, children, options = {}) => {
        const { touchCache = true } = options;
        if (!sectionUuid) {
            return;
        }
        const safeChildren = Array.isArray(children) ? children : [];
        setChildrenMap((prev) => ({ ...prev, [sectionUuid]: safeChildren }));
        setSectionStats((prev) => ({ ...prev, [sectionUuid]: toCounterStats(safeChildren) }));
        setChildLoadedBySection((prev) => ({ ...prev, [sectionUuid]: true }));
        setChildLoadingBySection((prev) => ({ ...prev, [sectionUuid]: false }));
        if (touchCache) {
            childrenCacheRef.current.set(sectionUuid, {
                data: safeChildren,
                fetchedAt: Date.now()
            });
        }
    }, [toCounterStats]);

    const readCachedChildren = useCallback((sectionUuid) => {
        const cachedEntry = childrenCacheRef.current.get(sectionUuid);
        if (!cachedEntry) {
            return null;
        }
        if ((Date.now() - cachedEntry.fetchedAt) > CHILDREN_CACHE_TTL_MS) {
            childrenCacheRef.current.delete(sectionUuid);
            return null;
        }
        return Array.isArray(cachedEntry.data) ? cachedEntry.data : [];
    }, []);

    const fetchChildrenForSection = useCallback(async (sectionUuid, options = {}) => {
        const { force = false, silent = false } = options;
        const normalizedSectionUuid = String(sectionUuid || '').trim();
        if (!normalizedSectionUuid) {
            return [];
        }

        if (!force) {
            const cachedChildren = readCachedChildren(normalizedSectionUuid);
            if (cachedChildren) {
                writeChildrenState(normalizedSectionUuid, cachedChildren, { touchCache: false });
                return cachedChildren;
            }
        }

        const inFlightRequest = childrenInFlightRef.current.get(normalizedSectionUuid);
        if (inFlightRequest) {
            return inFlightRequest;
        }

        if (!silent) {
            setChildLoadingBySection((prev) => ({ ...prev, [normalizedSectionUuid]: true }));
        }

        const requestPromise = fetchChildrenRef.current(normalizedSectionUuid)
            .then((response) => {
                const children = Array.isArray(response) ? response : [];
                writeChildrenState(normalizedSectionUuid, children);
                logger.debug(CHILDREN_LOG_MODULE, 'Children loaded', {
                    source: { file: 'features/hierarchy/hooks/useStoreManageController.js', method: 'fetchChildrenForSection' },
                    parentSectionUuid: normalizedSectionUuid,
                    count: children.length
                });
                return children;
            })
            .catch((error) => {
                setChildLoadingBySection((prev) => ({ ...prev, [normalizedSectionUuid]: false }));
                throw error;
            })
            .finally(() => {
                childrenInFlightRef.current.delete(normalizedSectionUuid);
            });

        childrenInFlightRef.current.set(normalizedSectionUuid, requestPromise);
        return requestPromise;
    }, [readCachedChildren, writeChildrenState]);

    const toggleExpand = useCallback((section) => {
        const sectionUuid = String(section?.sectionUuid || '').trim();
        if (!sectionUuid) {
            return;
        }
        let shouldLoadChildren = false;
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(sectionUuid)) {
                next.delete(sectionUuid);
            } else {
                next.add(sectionUuid);
                shouldLoadChildren = true;
            }
            return next;
        });
        if (shouldLoadChildren) {
            fetchChildrenForSection(sectionUuid, { force: true, silent: false }).catch((error) => {
                const message = error?.data?.message || error?.message || 'Unable to load counters.';
                onToast?.({ message, variant: 'error' });
            });
        }
    }, [fetchChildrenForSection, onToast]);

    const handleFieldChange = (field, value) => {
        setSectionFormError('');
        setFormValues((prev) => {
            if (field === 'floorNumber') {
                return { ...prev, floorNumber: value, sectionWing: '' };
            }
            return { ...prev, [field]: value };
        });
    };

    const parseFloorNumber = useCallback((value) => {
        const rawValue = value == null ? '' : String(value).trim();
        if (!rawValue) {
            return null;
        }
        const floorNumber = Number(rawValue);
        if (!Number.isInteger(floorNumber) || floorNumber < 0) {
            return null;
        }
        return floorNumber;
    }, []);

    const loadZonesForFloor = useCallback(async (floorNumber, options = {}) => {
        const { silent = false } = options;
        if (floorNumber == null || !storeUuid) {
            return [];
        }
        if (!silent) {
            setIsZoneLoading(true);
        }
        try {
            const zones = await fetchZones(floorNumber);
            setZonesByFloor((prev) => ({ ...prev, [floorNumber]: zones }));
            return zones;
        } catch (error) {
            if (!silent) {
                const message = error?.data?.message || error?.message || 'Unable to load zones.';
                onToast?.({ message, variant: 'error' });
            }
            return [];
        } finally {
            if (!silent) {
                setIsZoneLoading(false);
            }
        }
    }, [fetchZones, onToast, storeUuid]);

    const currentFloorNumber = useMemo(() => parseFloorNumber(formValues.floorNumber), [formValues.floorNumber, parseFloorNumber]);
    const floorZones = useMemo(() => {
        if (currentFloorNumber == null) {
            return [];
        }
        return zonesByFloor[currentFloorNumber] || [];
    }, [currentFloorNumber, zonesByFloor]);
    const zoneModalFloorNumber = useMemo(() => parseFloorNumber(zoneFormValues.floorNumber), [zoneFormValues.floorNumber, parseFloorNumber]);
    const zoneModalFloorZones = useMemo(() => {
        if (zoneModalFloorNumber == null) {
            return [];
        }
        return zonesByFloor[zoneModalFloorNumber] || [];
    }, [zoneModalFloorNumber, zonesByFloor]);

    useEffect(() => {
        if (!isModalOpen || currentFloorNumber == null) {
            return;
        }
        loadZonesForFloor(currentFloorNumber, { silent: true }).catch(() => undefined);
    }, [currentFloorNumber, isModalOpen, loadZonesForFloor]);

    const editFloorNumber = useMemo(() => parseFloorNumber(editFormValues.floorNumber), [editFormValues.floorNumber, parseFloorNumber]);
    const editFloorZones = useMemo(() => {
        if (editFloorNumber == null) {
            return [];
        }
        return zonesByFloor[editFloorNumber] || [];
    }, [editFloorNumber, zonesByFloor]);

    useEffect(() => {
        if (!isEditModalOpen || editFloorNumber == null) {
            return;
        }
        loadZonesForFloor(editFloorNumber, { silent: true }).catch(() => undefined);
    }, [editFloorNumber, isEditModalOpen, loadZonesForFloor]);

    useEffect(() => {
        if (!isZoneModalOpen || zoneModalFloorNumber == null) {
            return;
        }
        loadZonesForFloor(zoneModalFloorNumber, { silent: true }).catch(() => undefined);
    }, [isZoneModalOpen, zoneModalFloorNumber, loadZonesForFloor]);

    const resetForm = () => {
        setFormValues({
            sectionType: '',
            sectionWindows: '',
            sectionWing: '',
            floorNumber: '',
            gracePeriodMins: ''
        });
    };

    const openModal = () => {
        resetForm();
        setSectionFormError('');
        setIsModalOpen(true);
    };

    const closeModal = () => {
        if (isSaving) {
            return;
        }
        setIsModalOpen(false);
    };

    const openZoneModal = () => {
        setZoneFormError('');
        setZoneFormValues({
            floorNumber: formValues.floorNumber || '',
            zoneName: ''
        });
        setIsZoneModalOpen(true);
    };

    const closeZoneModal = () => {
        if (isZoneSaving) {
            return;
        }
        setIsZoneModalOpen(false);
    };

    const handleZoneFormChange = (field, value) => {
        setZoneFormError('');
        setZoneFormValues((prev) => ({ ...prev, [field]: value }));
    };

    const handleCreateZone = async () => {
        setZoneFormError('');
        if (!storeUuid) {
            setZoneFormError('Store not found. Please login again.');
            return;
        }
        const floorNumber = parseFloorNumber(zoneFormValues.floorNumber);
        if (floorNumber == null) {
            setZoneFormError('Select a valid floor number before adding a zone.');
            return;
        }
        const zoneName = serializeZoneName(zoneFormValues.zoneName);
        if (!zoneName) {
            setZoneFormError('Zone name is required.');
            return;
        }
        const alreadyExists = zoneModalFloorZones.some(
            (zone) => normalizeZoneName(zone.zoneName) === normalizeZoneName(zoneName)
        );
        if (alreadyExists) {
            setZoneFormError('Zone already exists for this floor.');
            return;
        }
        setIsZoneSaving(true);
        try {
            const response = await createZone({
                storeUuid,
                floorNumber,
                zoneName
            }, {
                skipErrorToast: true,
                skipSuccessToast: true
            });
            const zones = await loadZonesForFloor(floorNumber, { silent: true });
            const resolvedZoneName = response?.data?.zoneName
                || zones.find((zone) => normalizeZoneName(zone.zoneName) === normalizeZoneName(zoneName))?.zoneName
                || zoneName;
            if (parseFloorNumber(formValues.floorNumber) === floorNumber) {
                setFormValues((prev) => ({ ...prev, sectionWing: resolvedZoneName }));
            }
            setZoneFormValues({ floorNumber: String(floorNumber), zoneName: '' });
            setZoneFormError('');
            setIsZoneModalOpen(false);
            onToast?.({ message: 'Zone added successfully.', variant: 'success' });
        } catch (error) {
            const message = Number(error?.status) === 409
                ? 'Zone already exists for this floor.'
                : (error?.data?.message || error?.message || 'Unable to add zone.');
            setZoneFormError(message);
            onToast?.({ message, variant: 'error' });
        } finally {
            setIsZoneSaving(false);
        }
    };

    const resolveCounterType = (sectionType) => {
        if (sectionType === 'BILLING_SECTION') {
            return 'BILLING_COUNTER';
        }
        if (sectionType === 'TRIAL_SECTION') {
            return 'TRIAL_ROOM';
        }
        return 'EXCHANGE_SECTION';
    };

    const fetchCounters = async (section) => {
        if (!section?.sectionUuid) {
            return;
        }
        setIsCounterLoading(true);
        try {
            const list = await fetchChildrenForSection(section.sectionUuid, { force: true, silent: false });
            setCounterList(list);
        } catch (error) {
            setCounterList([]);
        } finally {
            setIsCounterLoading(false);
        }
    };

    const openCounterModal = (section) => {
        setCounterSection(section);
        setCounterForm({ count: 1 });
        setFilterStatus('ALL');
        setSortKey('NAME');
        setDeleteTarget(null);
        const cachedChildren = readCachedChildren(section?.sectionUuid);
        setCounterList(Array.isArray(cachedChildren) ? cachedChildren : []);
        setIsCounterModalOpen(true);
        fetchCounters(section);
    };

    const closeCounterModal = () => {
        if (isCounterSaving) {
            return;
        }
        setIsCounterModalOpen(false);
        setCounterSection(null);
        setCounterList([]);
    };

    const handleCounterFormChange = (field, value) => {
        setCounterFormError('');
        setCounterForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleToggleCounter = async (counter) => {
        if (!counter?.sectionUuid) {
            return;
        }
        try {
            await setCounterVisibility(counter.sectionUuid, counter.rowStatus === 1);
            setCounterList((prev) => {
                const next = prev.map((item) => (
                    item.sectionUuid === counter.sectionUuid
                        ? { ...item, rowStatus: counter.rowStatus === 1 ? 0 : 1 }
                        : item
                ));
                if (counterSection?.sectionUuid) {
                    writeChildrenState(counterSection.sectionUuid, next);
                }
                return next;
            });
        } catch (error) {
            const message = error?.data?.message || error?.message || 'Unable to update counter status.';
            onToast?.({ message, variant: 'error' });
        }
    };

    const handleInlineToggle = async (counter, parentUuid) => {
        if (!counter?.sectionUuid) {
            return;
        }
        try {
            await setCounterVisibility(counter.sectionUuid, counter.rowStatus === 1);
            const currentChildren = childrenMap[parentUuid] || readCachedChildren(parentUuid) || [];
            const nextChildren = currentChildren.map((item) => (
                item.sectionUuid === counter.sectionUuid
                    ? { ...item, rowStatus: counter.rowStatus === 1 ? 0 : 1 }
                    : item
            ));
            writeChildrenState(parentUuid, nextChildren);
            if (counterSection?.sectionUuid === parentUuid) {
                setCounterList(nextChildren);
            }
        } catch (error) {
            const message = error?.data?.message || error?.message || 'Unable to update counter status.';
            onToast?.({ message, variant: 'error' });
        }
    };

    const handleDeleteCounter = async () => {
        if (!deleteTarget?.sectionUuid) {
            return;
        }
        try {
            await deleteCounter(deleteTarget.sectionUuid);
            setCounterList((prev) => {
                const next = prev.filter((item) => item.sectionUuid !== deleteTarget.sectionUuid);
                if (counterSection?.sectionUuid) {
                    writeChildrenState(counterSection.sectionUuid, next);
                }
                return next;
            });
            setDeleteTarget(null);
        } catch (error) {
            const message = error?.data?.message || error?.message || 'Unable to delete counter.';
            onToast?.({ message, variant: 'error' });
        }
    };

    const handleAddCounters = async () => {
        setCounterFormError('');
        const count = Number(counterForm.count);
        if (!count || Number.isNaN(count) || count <= 0) {
            setCounterFormError('Counter count must be at least 1.');
            return;
        }
        if (!counterSection) {
            return;
        }
        const baseName = (counterSection.sectionName || activeCounterMeta.singleUnitLabel || 'Counter').trim();
        const nextCounterNumber = (() => {
            const prefix = `${baseName} `;
            let max = 0;
            counterList.forEach((counter) => {
                const currentName = (counter?.sectionName || '').trim();
                if (currentName === baseName) {
                    max = Math.max(max, 1);
                    return;
                }
                if (!currentName.startsWith(prefix)) {
                    return;
                }
                const suffix = currentName.slice(prefix.length).trim();
                const parsed = Number(suffix);
                if (Number.isInteger(parsed) && parsed > 0) {
                    max = Math.max(max, parsed);
                }
            });
            return max > 0 ? max + 1 : counterList.length + 1;
        })();
        const counterType = resolveCounterType(counterSection.sectionType);
        setIsCounterSaving(true);
        try {
            const created = [];
            for (let index = 0; index < count; index += 1) {
                const name = `${baseName} ${nextCounterNumber + index}`;
                const response = await addChild({
                    sectionName: name,
                    sectionType: counterType,
                    storeUuid,
                    mallUuid: storeMallUuid,
                    floorNumber: counterSection.floorNumber != null ? counterSection.floorNumber : 0,
                    sectionWing: counterSection.sectionWing || '',
                    parentSectionUuid: counterSection.sectionUuid,
                    gracePeriodMins: counterSection.gracePeriodMins || 5,
                    isSubSection: true
                });
                if (response?.data) {
                    created.push(response.data);
                }
            }
            onToast?.({ message: 'Counters added successfully.', variant: 'success' });
            const updatedChildren = await fetchChildrenForSection(counterSection.sectionUuid, { force: true, silent: true });
            setCounterList(updatedChildren);
            setCounterForm({ count: 1 });
            setCounterFormError('');
        } catch (error) {
            const message = error?.data?.message || error?.message || 'Unable to add counters.';
            setCounterFormError(message);
            onToast?.({ message, variant: 'error' });
        } finally {
            setIsCounterSaving(false);
        }
    };

    useEffect(() => {
        const validParentUuids = new Set(parentSectionUuids);
        setChildrenMap((prev) => Object.fromEntries(
            Object.entries(prev).filter(([sectionUuid]) => validParentUuids.has(sectionUuid))
        ));
        setSectionStats((prev) => Object.fromEntries(
            Object.entries(prev).filter(([sectionUuid]) => validParentUuids.has(sectionUuid))
        ));
        setChildLoadingBySection((prev) => Object.fromEntries(
            Object.entries(prev).filter(([sectionUuid]) => validParentUuids.has(sectionUuid))
        ));
        setChildLoadedBySection((prev) => Object.fromEntries(
            Object.entries(prev).filter(([sectionUuid]) => validParentUuids.has(sectionUuid))
        ));
        Array.from(childrenCacheRef.current.keys()).forEach((sectionUuid) => {
            if (!validParentUuids.has(sectionUuid)) {
                childrenCacheRef.current.delete(sectionUuid);
            }
        });
        Array.from(childrenInFlightRef.current.keys()).forEach((sectionUuid) => {
            if (!validParentUuids.has(sectionUuid)) {
                childrenInFlightRef.current.delete(sectionUuid);
            }
        });
    }, [parentSectionUuids]);

    useEffect(() => {
        if (!parentSectionUuids.length) {
            return;
        }
        let isCancelled = false;
        const prefetchChildrenForStats = async () => {
            await Promise.allSettled(
                parentSectionUuids.map(async (sectionUuid) => {
                    if (!sectionUuid || isCancelled) {
                        return;
                    }
                    await fetchChildrenForSection(sectionUuid, { force: false, silent: true });
                })
            );
        };
        prefetchChildrenForStats().catch((error) => {
            logger.warn(CHILDREN_LOG_MODULE, 'Background children prefetch failed', {
                source: { file: 'features/hierarchy/hooks/useStoreManageController.js', method: 'prefetchChildrenForStats' },
                error: error instanceof Error ? error.message : String(error)
            });
        });
        return () => {
            isCancelled = true;
        };
    }, [parentSectionUuids, fetchChildrenForSection]);

    useEffect(() => {
        const isOpen = isModalOpen
            || isCounterModalOpen
            || showSettingsSaveModal
            || isEditModalOpen
            || Boolean(deleteSectionTarget)
            || isZoneModalOpen
            || sectionCreateConflict.open;
        if (isOpen) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [isModalOpen, isCounterModalOpen, showSettingsSaveModal, isEditModalOpen, deleteSectionTarget, isZoneModalOpen, sectionCreateConflict.open]);

    useEffect(() => {
        if (!resolvedQueueSettingsToken) {
            return;
        }
        fetchQueueSettingsRef.current().catch(() => undefined);
    }, [resolvedQueueSettingsToken]);

    const displayCounters = useMemo(() => {
        let list = counterList.slice();
        if (filterStatus === 'ACTIVE') {
            list = list.filter((c) => c.rowStatus === 1);
        } else if (filterStatus === 'HIDDEN') {
            list = list.filter((c) => c.rowStatus === 0);
        }
        if (sortKey === 'NAME') {
            list.sort((a, b) => (a.sectionName || '').localeCompare(b.sectionName || ''));
        } else if (sortKey === 'STATUS') {
            list.sort((a, b) => (b.rowStatus || 0) - (a.rowStatus || 0));
        }
        return list;
    }, [counterList, filterStatus, sortKey]);

    const handleSave = async () => {
        setSectionFormError('');
        const sectionType = formValues.sectionType.trim();
        const sectionWing = formValues.sectionWing.trim();
        const sectionWindows = Number(formValues.sectionWindows);
        const floorNumber = parseFloorNumber(formValues.floorNumber);
        const gracePeriodMins = Number(formValues.gracePeriodMins);

        if (!sectionType) {
            setSectionFormError('Section type is required.');
            return;
        }

        // Auto-generate sectionName based on type
        const typeMeta = sectionTypesData[sectionType];
        const generatedSectionName = typeMeta ? `${typeMeta.label} Section` : 'Section';
        if (floorNumber == null) {
            setSectionFormError('Floor number is required.');
            return;
        }
        if (!sectionWing) {
            setSectionFormError('Select a zone. Add one first if no zones exist for this floor.');
            return;
        }
        const zoneExistsForFloor = floorZones.some((zone) => normalizeZoneName(zone.zoneName) === normalizeZoneName(sectionWing));
        if (!zoneExistsForFloor) {
            setSectionFormError('Select a registered zone for this floor or add a new zone first.');
            return;
        }
        if (!gracePeriodMins || Number.isNaN(gracePeriodMins) || gracePeriodMins <= 0) {
            setSectionFormError('Grace period must be greater than 0.');
            return;
        }
        if (!sectionWindows || Number.isNaN(sectionWindows) || sectionWindows <= 0) {
            setSectionFormError('Section windows must be greater than 0.');
            return;
        }
        if (!storeUuid) {
            setSectionFormError('Store not found. Please login again.');
            return;
        }

        const sectionPayload = {
            sectionName: generatedSectionName,
            sectionType,
            sectionWindows,
            sectionWing,
            storeUuid,
            mallUuid: storeMallUuid,
            floorNumber,
            gracePeriodMins
        };

        const completeSectionSave = async () => {
            setIsModalOpen(false);
            setSectionFormError('');
            resetForm();
            await onSectionsUpdated?.();
            onZoneSaved?.();
        };

        setIsSaving(true);
        try {
            await createSection(sectionPayload, { skipErrorToast: true });
            await completeSectionSave();
        } catch (error) {
            if (Number(error?.status) === 409) {
                const conflictQuestion = error?.data?.data?.question
                    || error?.data?.question
                    || SECTION_WING_CONFLICT_CONFIRMATION_QUESTION;
                setSectionCreateConflict({
                    open: true,
                    question: conflictQuestion,
                    payload: sectionPayload
                });
                return;
            }
            const message = error?.data?.message || error?.message || 'Unable to add a section. Please try again.';
            setSectionFormError(message);
            onToast?.({ message, variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const closeSectionCreateConflict = () => {
        if (isSectionCreateConflictSaving) {
            return;
        }
        setSectionCreateConflict(DEFAULT_SECTION_CREATE_CONFLICT);
    };

    const handleConfirmSectionCreateConflict = async () => {
        if (!sectionCreateConflict.payload) {
            closeSectionCreateConflict();
            return;
        }

        setIsSectionCreateConflictSaving(true);
        try {
            await createSection(
                { ...sectionCreateConflict.payload, forceCreate: true },
                { skipErrorToast: true }
            );
            setSectionCreateConflict(DEFAULT_SECTION_CREATE_CONFLICT);
            setIsModalOpen(false);
            resetForm();
            await onSectionsUpdated?.();
            onZoneSaved?.();
        } catch (error) {
            const message = error?.data?.message || error?.message || 'Unable to add a section. Please try again.';
            onToast?.({ message, variant: 'error' });
        } finally {
            setIsSectionCreateConflictSaving(false);
        }
    };

    const handleEditSection = (section) => {
        setEditFormError('');
        setEditSection(section);
        setEditFormValues({
            sectionName: section.sectionName || '',
            sectionType: section.sectionType || '',
            sectionWing: section.sectionWing || '',
            floorNumber: section.floorNumber != null ? String(section.floorNumber) : '',
            gracePeriodMins: section.gracePeriodMins != null ? String(section.gracePeriodMins) : '',
            sectionWindows: section.sectionWindows != null ? String(section.sectionWindows) : ''
        });
        setIsEditModalOpen(true);
    };

    const handleEditFormChange = (field, value) => {
        setEditFormError('');
        setEditFormValues((prev) => ({ ...prev, [field]: value }));
    };

    const handleUpdateSection = async () => {
        if (!editSection?.sectionUuid) {
            return;
        }
        setEditFormError('');
        setIsEditSaving(true);
        try {
            const trimmedName = editFormValues.sectionName.trim();
            if (trimmedName && trimmedName.length < 3) {
                setEditFormError('Section name must be at least 3 characters.');
                return;
            }
            const body = {};
            if (trimmedName) {
                body.sectionName = trimmedName;
            }
            if (editFormValues.sectionType.trim()) {
                body.sectionType = editFormValues.sectionType.trim();
            }
            if (editFormValues.sectionWing.trim()) {
                body.sectionWing = editFormValues.sectionWing.trim();
            }
            const floor = Number(editFormValues.floorNumber);
            if (!Number.isNaN(floor)) {
                if (floor < 0) {
                    setEditFormError('Floor number must be 0 or above.');
                    return;
                }
                body.floorNumber = floor;
            }
            const grace = Number(editFormValues.gracePeriodMins);
            if (!Number.isNaN(grace)) {
                if (grace <= 0) {
                    setEditFormError('Grace period must be greater than 0.');
                    return;
                }
                body.gracePeriodMins = grace;
            }
            const windows = Number(editFormValues.sectionWindows);
            if (!Number.isNaN(windows)) {
                if (windows <= 0) {
                    setEditFormError('Section windows must be greater than 0.');
                    return;
                }
                body.sectionWindows = windows;
            }
            await updateSection(editSection.sectionUuid, body);
            setIsEditModalOpen(false);
            setEditSection(null);
            setEditFormError('');
            await onSectionsUpdated?.();
        } catch (error) {
            const message = error?.data?.message || error?.message || 'Unable to update section.';
            setEditFormError(message);
            onToast?.({ message, variant: 'error' });
        } finally {
            setIsEditSaving(false);
        }
    };

    const handleToggleSectionStatus = async (section) => {
        if (!section?.sectionUuid) {
            return;
        }
        const isCurrentlyActive = Number(section?.rowStatus ?? 1) === 1;
        try {
            await setSectionVisibility(section.sectionUuid, isCurrentlyActive);
            await onSectionsUpdated?.();
            onToast?.({
                message: `Section ${isCurrentlyActive ? 'paused' : 'resumed'} successfully.`,
                variant: 'success'
            });
        } catch (error) {
            const message = error?.data?.message || error?.message || 'Unable to update section status.';
            onToast?.({ message, variant: 'error' });
        }
    };

    const handleDeleteSection = async () => {
        if (!deleteSectionTarget?.sectionUuid) {
            return;
        }
        setIsDeletingSection(true);
        try {
            await deleteSection(deleteSectionTarget.sectionUuid);
            setDeleteSectionTarget(null);
            await onSectionsUpdated?.();
        } catch (error) {
            const message = error?.data?.message || error?.message || 'Unable to delete section.';
            onToast?.({ message, variant: 'error' });
        } finally {
            setIsDeletingSection(false);
        }
    };

    const activeCounterMeta = sectionTypesData[counterSection?.sectionType] || {
        label: 'Section',
        unitLabel: 'Counters',
        singleUnitLabel: 'Counter',
        manageLabel: 'Manage',
        className: 'default'
    };
    const counterBaseName = (counterSection?.sectionName || activeCounterMeta.singleUnitLabel || 'Counter').trim();
    const nextCounterNumber = useMemo(() => {
        if (!counterSection?.sectionUuid) {
            return 1;
        }
        const prefix = `${counterBaseName} `;
        let max = 0;
        counterList.forEach((counter) => {
            const currentName = (counter?.sectionName || '').trim();
            if (currentName === counterBaseName) {
                max = Math.max(max, 1);
                return;
            }
            if (!currentName.startsWith(prefix)) {
                return;
            }
            const suffix = currentName.slice(prefix.length).trim();
            const parsed = Number(suffix);
            if (Number.isInteger(parsed) && parsed > 0) {
                max = Math.max(max, parsed);
            }
        });
        return max > 0 ? max + 1 : counterList.length + 1;
    }, [counterBaseName, counterList, counterSection?.sectionUuid]);

    const navigateTo = useCallback((path) => {
        if (!path) {
            return;
        }
        if (typeof onNavigate === 'function') {
            onNavigate(path);
            return;
        }
        window.history.pushState({}, '', path);
        window.dispatchEvent(new PopStateEvent('popstate'));
    }, [onNavigate]);

    const currentPage = String(page || 'landing');
    const showSections = currentPage === 'sections';
    const showQueue = currentPage === 'queue';
    const showHours = currentPage === 'hours';
    const showLanding = currentPage === 'landing';
    const showSettingsSave = showQueue || showHours;
    const merchantDisplayName = storeName || 'Your Store';

    const saveModalCopy = useMemo(() => {
        return 'Are you sure you want to save these settings? This will update your queue and operating-hours configuration.';
    }, []);

    const operationsSummary = useMemo(() => {
        let totalCounters = 0;
        let activeCounters = 0;
        let hiddenCounters = 0;

        parentSections.forEach((section) => {
            const stats = sectionStats[section.sectionUuid] || {};
            const fallbackTotal = Number(section.sectionWindows) || 0;
            const total = Number.isFinite(stats.total) ? stats.total : fallbackTotal;
            const active = Number.isFinite(stats.active) ? stats.active : total;
            const hidden = Number.isFinite(stats.hidden) ? stats.hidden : Math.max(total - active, 0);
            totalCounters += total;
            activeCounters += active;
            hiddenCounters += hidden;
        });

    return {
            sectionCount: parentSections.length,
            totalCounters,
            activeCounters,
            hiddenCounters
        };
    }, [parentSections, sectionStats]);

    const contextCopy = useMemo(() => {
        if (showSections) {
            return {
                label: 'Section Controls',
                title: 'Design service flow by zone, then keep counters healthy.',
                description: 'Create and tune billing, trial, and exchange sections so customer movement is predictable during peak hours.',
                note: 'Review hidden counters daily. Sustained hidden capacity usually signals staffing mismatch.'
            };
        }
        if (showQueue) {
            return {
                label: 'Queue Policy',
                title: 'Define queue SLA and automate edge-case behavior.',
                description: 'Set wait-time limits, re-queue policy, and priority surcharge so the queue remains fair and operationally stable.',
                note: `Current SLA threshold is ${queueSettings.longestWaitThreshold} minutes. Align this with floor staffing.`
            };
        }
        if (showHours) {
            return {
                label: 'Operating Window',
                title: 'Synchronize queue availability with store working hours.',
                description: 'Customers should only be able to join queue during staffed windows to avoid service debt and no-show spikes.',
                note: `Weekday schedule is ${queueSettings.weekdayOpen} - ${queueSettings.weekdayClosed}. Keep this aligned with opening announcements.`
            };
        }
        return {
            label: 'Manage Overview',
            title: `Control how ${merchantDisplayName} serves, queues, and operates.`,
            description: 'This workspace governs your service layout, queue policy, and operating schedule from one place.',
            note: 'Recommended setup order: configure sections, define queue rules, then finalize operating hours.'
        };
    }, [
        merchantDisplayName,
        queueSettings.longestWaitThreshold,
        queueSettings.weekdayClosed,
        queueSettings.weekdayOpen,
        showHours,
        showQueue,
        showSections
    ]);

    const landingCards = useMemo(() => ([
        {
            path: '/store/manage/sections',
            tag: 'Service Layout',
            title: 'Sections & Counters',
            subtitle: 'Structure billing, trial, and exchange zones with clear counter ownership.',
            metricLabel: 'Configured footprint',
            metricValue: `${operationsSummary.sectionCount} sections • ${operationsSummary.totalCounters} counters`,
            highlights: [
                `${operationsSummary.activeCounters} counters active`,
                `${operationsSummary.hiddenCounters} counters hidden`
            ],
            actionLabel: 'Open section controls'
        },
        {
            path: '/store/manage/queue',
            tag: 'Queue Policy',
            title: 'Queue Rules',
            subtitle: 'Define queue behavior for long waits, re-entry, and premium-priority handling.',
            metricLabel: 'Current queue SLA',
            metricValue: `${queueSettings.longestWaitThreshold} min longest wait`,
            highlights: [
                `Re-queueing ${queueSettings.requeueAllowed ? 'enabled' : 'disabled'}`,
                `Priority surcharge ₹${queueSettings.priorityPassSurcharge}`
            ],
            actionLabel: 'Open queue policy'
        },
        {
            path: '/store/manage/hours',
            tag: 'Operating Window',
            title: 'Operating Hours',
            subtitle: 'Keep queue open windows aligned with staff presence and customer expectation.',
            metricLabel: 'Weekly schedule',
            metricValue: `${queueSettings.weekdayOpen}-${queueSettings.weekdayClosed} (Mon-Fri)`,
            highlights: [
                `${queueSettings.weekendOpen}-${queueSettings.weekendClosed} (Sat-Sun)`,
                'Use save to publish time changes'
            ],
            actionLabel: 'Open operating hours'
        }
    ]), [operationsSummary, queueSettings]);

  return {
    isModalOpen,
    isSaving,
    formValues,
    isCounterModalOpen,
    counterSection,
    counterList,
    isCounterLoading,
    counterForm,
    isCounterSaving,
    deleteTarget,
    sectionStats,
    filterStatus,
    sortKey,
    expandedSections,
    childrenMap,
    childLoadingBySection,
    childLoadedBySection,
    editSection,
    isEditModalOpen,
    isEditSaving,
    editFormValues,
    editFloorNumber,
    editFloorZones,
    handleEditFormChange,
    deleteSectionTarget,
    isDeletingSection,
    isZoneModalOpen,
    zoneFormValues,
    zonesByFloor,
    zoneModalFloorZones,
    isZoneLoading,
    isZoneSaving,
    sectionFormError,
    zoneFormError,
    counterFormError,
    editFormError,
    sectionCreateConflict,
    isSectionCreateConflictSaving,
    showSettingsSaveModal,
    isGlobalRequestLoading,
    queueSettings,
    handleQueueSettingChange,
    isSettingsSaving,
    handleSettingsSave,
    parentSections,
    toggleExpand,
    handleFieldChange,
    openZoneModal,
    closeZoneModal,
    handleZoneFormChange,
    handleCreateZone,
    openModal,
    closeModal,
    openCounterModal,
    closeCounterModal,
    handleCounterFormChange,
    handleToggleCounter,
    handleInlineToggle,
    setDeleteTarget,
    handleDeleteCounter,
    handleAddCounters,
    displayCounters,
    handleSave,
    handleEditSection,
    handleToggleSectionStatus,
    handleUpdateSection,
    handleDeleteSection,
    closeSectionCreateConflict,
    handleConfirmSectionCreateConflict,
    activeCounterMeta,
    counterBaseName,
    nextCounterNumber,
    navigateTo,
    showSections,
    showQueue,
    showHours,
    showLanding,
    showSettingsSave,
    saveModalCopy,
    operationsSummary,
    contextCopy,
    landingCards,
    setShowSettingsSaveModal,
    setFilterStatus,
    setSortKey,
    setIsEditModalOpen,
    setEditSection,
    setDeleteSectionTarget,
    floorZones,
    handleDownloadQR
  };
};

export default useStoreManageController;
